<template>
  <div class="text-sm px-2 mt-4">
    <div
      class="px-2 flex flex-wrap items-baseline"
      :class="{ 'mb-4': timePeriod !== 'custom' }"
    >
      <span class="mr-4">{{$t('history.show')}}</span>
      <md-field class="flex-1 my-0">
        <md-select
          v-model="timePeriod"
          v-on:md-selected="getHistory()"
          name="timePeriod"
          id="timePeriod"
          class="font-semibold"
        >
          <md-option
            v-for="period in timePeriods"
            :key="period"
            :value="period"
          >{{$t(`history.${period}`)}}</md-option>
        </md-select>
      </md-field>
    </div>

    <div v-if="timePeriod === 'custom'" class="flex items-center px-2">
      <md-field class="flex-1 mr-4">
        <label>{{$t('history.from')}}</label>
        <md-input :value="dateFrom | formatDate" class="w-full"></md-input>
        <Datetime
          v-model="dateFrom"
          type="datetime"
          :min-datetime="minRange"
          :max-datetime="`${dateTo}` || now"
          :phrases="datetimeActions"
          :auto="true"
          :key="`from-${$i18n.locale}`"
          class="theme-primary absolute w-full h-8"
        ></Datetime>
      </md-field>
      <md-field class="flex-1 mr-2">
        <label>{{$t('history.to')}}</label>
        <md-input :value="dateTo | formatDate" class="w-full"></md-input>
        <Datetime
          v-model="dateTo"
          type="datetime"
          :min-datetime="`${dateFrom}` || minRange"
          :max-datetime="now"
          :phrases="datetimeActions"
          :auto="true"
          :key="`to-${$i18n.locale}`"
          class="theme-primary absolute w-full h-8"
        ></Datetime>
      </md-field>
      <Icon :icon="['far', 'fa-calendar-alt', 'text-base text-gray-500 w-6 text-center']"/>
    </div>

    <div
      class="transition-opacity duration-200 text-center"
      :class="{ 'opacity-50': isLoading }"
    >
      <ButtonToggle :options="sensors" v-on:change="showDataset($event)" class="my-2"></ButtonToggle>
      <BarChart
        v-if="datasets.datasets.length"
        ref="chart"
        :chartData="datasets"
        :styles="chartStyle"
      />
    </div>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import moment from 'moment';
import { Datetime } from 'vue-datetime';
import { Settings } from 'luxon';
import BarChart from '@/components/atoms/BarChart';
import Icon from '@/components/atoms/Icon';
import ButtonToggle from '@/components/atoms/ButtonToggle';

import 'vue-datetime/dist/vue-datetime.css';

const AIR_QUALITY_LEVELS = {
  'pm2.5': [0, 12, 36, 60, 84, 120],
  'pm10': [0, 20, 60, 100, 140, 200],
};

const COLORS = require('@/constants/colors.json')

export default {
  name: 'HistoryChart',

  components: {
    BarChart,
    Icon,
    Datetime,
    ButtonToggle,
  },

  props: {
    sensorId: {
      type: Number,
      default: null,
    },
  },

  data() {
    const timePeriods = ['last24h', 'custom'];

    return {
      chartStyle: { height: '12rem', position: 'relative' },
      timePeriod: null,
      timePeriods,
      now: moment().format(),
      dateFrom: null,
      dateTo: null,
      startDate: null,
      endDate: null,
    };
  },

  computed: {
    ...mapGetters([
      'measurements',
      'isLoading',
    ]),

    datasets() {
      if (!this.measurements || this.measurements.id !== this.sensorId || !this.sensors.length) {
        return { labels: [], datasets: [] };
      }

      const averages = this.constructDatasets() || [];
      const labels = this.constructLabels(averages[0]);
      const datasets = averages
        .map(dataset => ({
          ...dataset,
          data: Object.keys(dataset.data).map(key => +(dataset.data[key].toFixed(2))),
        }));

      return { datasets, labels };
    },

    sensors() {
      return !this.measurements
        ? []
        : ['pm2.5', 'pm10'].filter(type => Object.keys(this.measurements).includes(type));
    },

    datetimeActions() {
      return { ok: this.$t('ok'), cancel: this.$t('cancel') };
    },

    minRange() {
      return moment().subtract(12, 'months').format();
    },
  },

  filters: {
    formatDate(value) {
      return !value ? '' : moment(value).format('DD. MMM Y H:mm');
    },
  },

  watch: {
    '$route.params.lang': {
      immediate: true,
      handler (lang) {
        Settings.defaultLocale = lang;
        moment.locale(lang);
      }
    },

    sensorId: {
      immediate: true,
      handler (value) {
        this.startDate = null;
        this.endDate = null;

        if (!!value) {
          this.timePeriod = this.timePeriods[0];
          this.getHistory();
        }
      }
    },

    dateFrom() {
      this.getHistory();
    },

    dateTo() {
      this.getHistory();
    },
  },

  methods: {
    ...mapActions([
      'getHistoricalData',
    ]),

    getHistory() {
      const updateLastChange = this.timePeriod === 'last24h';
  
      if (updateLastChange) {
        this.resetDates();
      }

      const { sensorId, startDate, endDate, dateFrom, dateTo } = this;
      const start = +moment(dateFrom);
      const end = +moment(dateTo);

      if (start !== startDate || end !== endDate) {
        this.startDate = start;
        this.endDate = end;
        this.getHistoricalData({ start, end, id: sensorId, updateLastChange });
      }
    },

    constructDatasets() {
      return this.sensors.map(sensor => {
        const historicalData = this.measurements[sensor];
        const offset = moment(historicalData[historicalData.length - 1].date).diff(
          moment(historicalData[0].date),
          'days',
        ) > 7 ? 'day' : 'hour';
        let previousTimestamp = null;
        let count = 0;

        const data = historicalData
          .reduce(
            (average, history) => {
              const timestamp = +moment(history.date).startOf(offset);
              const sum = (average[timestamp] || 0) * count + history.value;

              count++;
              const avg = sum / count;

              if (timestamp !== previousTimestamp) {
                count = 0;
                previousTimestamp = timestamp;
              }

              return { ...average, [timestamp]: avg };
            },
            {},
          );
        const colors = Object.keys(data).map(key => this.valueToAirQualityColor(sensor, data[key]));
  
        return {
          label: sensor.toUpperCase(),
          data,
          backgroundColor: colors,
        };
      });
    },

    constructLabels(dataset) {
      return Object.keys(dataset.data).map(key => key);
    },

    valueToAirQualityColor(sensorType, value) {
      const thresholds = AIR_QUALITY_LEVELS[sensorType]
      let level = 0;
      while (level < thresholds.length && value > thresholds[level]) {
        level++;
      }
      return COLORS[level];
    },

    showDataset(dataset) {
      this.$refs.chart.setVisibleDataset(this.sensors.indexOf(dataset));
    },

    resetDates() {
      const dateTo = moment().format();
      const dateFrom = moment(dateTo).startOf('hour').subtract(24, 'hours').format();

      this.dateFrom = dateFrom;
      this.dateTo = dateTo;
    }
  },
};
</script>

<style lang="scss" scoped>
.md-field {
  &.my-0 {
    @apply .-mt-4 .mb-0;
  }

  &.pt-0 {
    padding-top: 0;
  }

  .md-input {
    @apply .text-sm;
  }
}
</style>
